import { useMediaQuery } from '@mantine/hooks';

export type FormFactor = 'desktop' | 'tablet' | 'handheld';

const DESKTOP_QUERY = '(min-width: 1024px)';
const TABLET_QUERY = '(min-width: 600px)';

/**
 * Evaluate a media query synchronously for the very first render. Guards the
 * (never-hit in this SPA, but safe) no-window case.
 */
function matchesNow(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;
}

/**
 * Returns the current form factor based on viewport width. Phase 2 §8.3:
 *   - handheld  : < 600 px  (phones, Zebra TC-class enterprise handhelds)
 *   - tablet    : 600 - 1023 px
 *   - desktop   : >= 1024 px
 *
 * Workflow components consume this to swap between handheld and desktop
 * variants of the same route.
 *
 * `getInitialValueInEffect: false` (plus a synchronous initial value) is
 * required: Mantine's default defers the real match to an effect and returns
 * `false` on the first render, which classified every fresh desktop load as
 * `handheld` until something forced a re-render (observed on
 * portal.invetrak.com, 2026-07-29). Reading matchMedia on first render fixes
 * that first paint.
 */
export function useViewport(): FormFactor {
  const isDesktop = useMediaQuery(DESKTOP_QUERY, matchesNow(DESKTOP_QUERY), {
    getInitialValueInEffect: false,
  });
  const isTabletOrLarger = useMediaQuery(TABLET_QUERY, matchesNow(TABLET_QUERY), {
    getInitialValueInEffect: false,
  });

  if (isDesktop) return 'desktop';
  if (isTabletOrLarger) return 'tablet';
  return 'handheld';
}
